import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, UserPlus, Mail, Lock, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Portal() {
  const [searchParams] = useSearchParams();
  const portalType = searchParams.get('type');
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    user_type: portalType === 'agent' ? 'agent' : 'investor',
    company: ''
  });

  // Update register data when portal type changes from URL
  useEffect(() => {
    if (portalType) {
      setRegisterData(prev => ({
        ...prev,
        user_type: portalType === 'agent' ? 'agent' : 'investor'
      }));
    }
  }, [portalType]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, registerData);
      toast.success('Registration successful! Please log in.');
      setIsLogin(true);
      setRegisterData({ email: '', password: '', user_type: 'investor', company: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="portal-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-md mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 data-testid="portal-title" className="text-4xl md:text-5xl font-black tracking-tighter uppercase mb-4">
            {portalType === 'agent' ? 'AGENT' : portalType === 'investor' ? 'INVESTOR' : 'PORTAL'} <span className="text-primary">{isLogin ? 'LOGIN' : 'REGISTER'}</span>
          </h1>
          <p data-testid="portal-subtitle" className="text-muted-foreground">
            {isLogin 
              ? `Access your ${portalType === 'agent' ? 'agent' : portalType === 'investor' ? 'investor' : 'investor or agent'} portal`
              : `Create your ${portalType === 'agent' ? 'agent' : 'investor'} account to get started`
            }
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="portal-tabs"
          className="flex gap-2 mb-8"
        >
          <button
            onClick={() => setIsLogin(true)}
            data-testid="login-tab-btn"
            className={`flex-1 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              isLogin
                ? 'bg-primary text-black font-bold'
                : 'bg-transparent border border-primary/50 text-primary hover:bg-primary/10'
            }`}
          >
            <LogIn size={18} className="inline mr-2" />
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            data-testid="register-tab-btn"
            className={`flex-1 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              !isLogin
                ? 'bg-primary text-black font-bold'
                : 'bg-transparent border border-primary/50 text-primary hover:bg-primary/10'
            }`}
          >
            <UserPlus size={18} className="inline mr-2" />
            Register
          </button>
        </motion.div>

        {/* Forms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass p-8"
        >
          {isLogin ? (
            <form onSubmit={handleLogin} data-testid="login-form" className="space-y-6">
              <div>
                <label htmlFor="login-email" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  id="login-email"
                  data-testid="login-email-input"
                  required
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  <Lock size={16} className="inline mr-2" />
                  Password
                </label>
                <input
                  type="password"
                  id="login-password"
                  data-testid="login-password-input"
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'LOGGING IN...' : 'LOGIN'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} data-testid="register-form" className="space-y-6">
              <div>
                <label htmlFor="register-email" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  id="register-email"
                  data-testid="register-email-input"
                  required
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="register-password" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  <Lock size={16} className="inline mr-2" />
                  Password
                </label>
                <input
                  type="password"
                  id="register-password"
                  data-testid="register-password-input"
                  required
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label htmlFor="user-type" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Account Type
                </label>
                <select
                  id="user-type"
                  data-testid="register-user-type-select"
                  value={registerData.user_type}
                  onChange={(e) => setRegisterData({ ...registerData, user_type: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                >
                  <option value="investor">Investor</option>
                  <option value="agent">Agent/Reseller</option>
                </select>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  <Building2 size={16} className="inline mr-2" />
                  Company (Optional)
                </label>
                <input
                  type="text"
                  id="company"
                  data-testid="register-company-input"
                  value={registerData.company}
                  onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                  placeholder="Your Company Name"
                />
              </div>

              <button
                type="submit"
                data-testid="register-submit-btn"
                disabled={loading}
                className="w-full px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
              </button>
            </form>
          )}
        </motion.div>

        {/* Info Boxes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 grid grid-cols-1 gap-4"
        >
          <div className="glass p-6">
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary mb-2">Investor Portal</h3>
            <p className="text-xs text-muted-foreground">
              Access investment opportunities, portfolio tracking, and financial reports.
            </p>
          </div>
          <div className="glass p-6">
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary mb-2">Agent Portal</h3>
            <p className="text-xs text-muted-foreground">
              Exclusive resources, training materials, sales support, and commission tracking.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
